import { isTauri } from "./isTauri";

/**
 * What a Codex thread id is allowed to contain. Anchored, and bounded — the id
 * is interpolated into a `codex://` URL, so `task/../../settings` must never
 * survive it.
 */
const CODEX_THREAD_ID = /^[A-Za-z0-9_-]{1,128}$/;

export type ReviewRuntimeOutcome = {
  supported: boolean;
  started: boolean;
  alreadyRunning: boolean;
  message: string;
};

export type ReviewRuntimeTarget = {
  url: string;
  runtimeProfileKey?: string;
  workspacePath?: string;
};

export async function ensureReviewRuntime(target: ReviewRuntimeTarget): Promise<ReviewRuntimeOutcome> {
  if (!isTauri() || !target.runtimeProfileKey || !target.workspacePath) {
    return { supported: false, started: false, alreadyRunning: false, message: "No local runtime profile is attached." };
  }
  const { invoke } = await import("@tauri-apps/api/core");
  return await invoke<ReviewRuntimeOutcome>("review_runtime_ensure", {
    profileKey: target.runtimeProfileKey,
    url: target.url,
    workspacePath: target.workspacePath,
  });
}

async function openUrl(url: URL): Promise<void> {
  if (isTauri()) {
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(url.toString());
    return;
  }
  const anchor = document.createElement("a");
  anchor.href = url.toString();
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();
}

export async function openExternalUrl(raw: string): Promise<void> {
  const url = new URL(raw);
  if (url.protocol !== "http:" && url.protocol !== "https:" && url.protocol !== "mailto:") {
    throw new Error("Only http(s) and mailto URLs can be opened.");
  }
  if (url.username || url.password) {
    throw new Error("Review URLs containing credentials are refused.");
  }
  await openUrl(url);
}

/**
 * Downloads an Inbox attachment and selects it in Finder.
 *
 * A PocketBase attachment can be cross-origin to the Tauri webview. WebKit is
 * then allowed to ignore an anchor's `download` attribute and render HTML in
 * the app window, so this intentionally goes through the native shell.
 */
export async function revealInboxAttachment(rawUrl: string, filename: string): Promise<void> {
  if (!isTauri()) {
    const anchor = document.createElement("a");
    anchor.href = rawUrl;
    anchor.download = filename;
    anchor.click();
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("download_and_reveal", { url: rawUrl, filename });
}

/**
 * Opens a Codex thread in the desktop client.
 *
 * Deliberately **not** routed through {@link openExternalUrl}: that function
 * refuses every scheme except http, https and mailto, and it is right to — it
 * is handed URLs that came out of a database. This one builds its own URL from
 * an id it has already validated against {@link CODEX_THREAD_ID}, so the
 * scheme is ours rather than the caller's.
 *
 * It currently has no call site in `app/src`. That is not a reason to delete
 * it: it is the client half of the AgentDesk secretary work, its tests still
 * pin the traversal guard (`task/../../settings` must throw), and an
 * unreferenced export is not the same thing as dead code while the feature it
 * belongs to is still being built.
 */
export async function openCodexThread(threadId: string): Promise<void> {
  if (!CODEX_THREAD_ID.test(threadId)) {
    throw new Error("The Secretary task ID is invalid.");
  }
  await openUrl(new URL(`codex://threads/${threadId}`));
}
